import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Globe } from "lucide-react";
import { TldCatalogForm } from "@/components/admin/tld-catalog";
import {
  useAdminTldCatalog,
  useCreateTldCatalog,
  useUpdateTldCatalog,
  useSetTldEnabled,
} from "@/hooks/useAdminTldCatalogQueries";
import { formatCurrency } from "@/lib/currency";
import type { TldCatalogDto } from "@/types/domain";
import type { TldCatalogFormData } from "@/schemas/adminTldCatalog.schema";

export function TldCatalogListPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTld, setEditingTld] = useState<TldCatalogDto | null>(null);

  const { data, isLoading, error, refetch } = useAdminTldCatalog();
  const createTld = useCreateTldCatalog();
  const updateTld = useUpdateTldCatalog();
  const setEnabled = useSetTldEnabled();

  const tlds = data ?? [];
  const enabledCount = tlds.filter((t) => t.isEnabled).length;

  const openCreateDialog = () => {
    setEditingTld(null);
    setDialogOpen(true);
  };

  const openEditDialog = (tld: TldCatalogDto) => {
    setEditingTld(tld);
    setDialogOpen(true);
  };

  const handleSubmit = (formData: TldCatalogFormData) => {
    if (editingTld) {
      // The `tld` field is immutable — exclude it from the update body.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tld: _tld, ...updateBody } = formData;
      updateTld.mutate(
        { id: editingTld.id, data: updateBody },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingTld(null);
          },
        },
      );
    } else {
      createTld.mutate(formData, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  const handleToggleEnabled = (tld: TldCatalogDto) => {
    setEnabled.mutate({ id: tld.id, isEnabled: !tld.isEnabled });
  };

  const isSubmitting = createTld.isPending || updateTld.isPending;

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="TLD Catalog"
          subtitle="Manage sellable top-level domains and retail pricing"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-destructive-ink">
                Failed to load the TLD catalog
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="TLD Catalog"
        subtitle="Manage sellable top-level domains and retail pricing"
        actions={[
          {
            label: "Create TLD",
            onClick: openCreateDialog,
            icon: <Plus className="mr-2 h-4 w-4" />,
          },
        ]}
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total TLDs</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tlds.length}</div>
            <p className="text-xs text-muted-foreground">Catalog entries</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enabled TLDs</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{enabledCount}</div>
            <p className="text-xs text-muted-foreground">
              Sellable to customers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Data Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>TLD Catalog ({tlds.length})</CardTitle>
          <CardDescription>
            All curated top-level domains with retail pricing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                Loading the TLD catalog...
              </p>
            </div>
          ) : tlds.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No TLDs in the catalog</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Get started by creating your first TLD
              </p>
              <Button
                onClick={openCreateDialog}
                variant="outline"
                className="mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First TLD
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">TLD</TableHead>
                  <TableHead scope="col">Display Name</TableHead>
                  <TableHead scope="col">Registrar</TableHead>
                  <TableHead scope="col">Registration</TableHead>
                  <TableHead scope="col">Renewal</TableHead>
                  <TableHead scope="col">Cost</TableHead>
                  <TableHead scope="col">Featured</TableHead>
                  <TableHead scope="col">Enabled</TableHead>
                  <TableHead scope="col" className="text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tlds.map((tld) => (
                  <TableRow key={tld.id}>
                    <TableCell className="font-medium">{tld.tld}</TableCell>
                    <TableCell>{tld.displayName}</TableCell>
                    <TableCell>{tld.registrarProvider}</TableCell>
                    <TableCell>
                      {formatCurrency(tld.registrationPrice, tld.currency)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(tld.renewalPrice, tld.currency)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(tld.registrarCost, tld.currency)}
                    </TableCell>
                    <TableCell>
                      {tld.isFeatured ? (
                        <Badge variant="secondary">Featured</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={tld.isEnabled}
                        onCheckedChange={() => handleToggleEnabled(tld)}
                        disabled={
                          setEnabled.isPending &&
                          setEnabled.variables?.id === tld.id
                        }
                        aria-label={`Toggle ${tld.tld} enabled`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(tld)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTld ? `Edit ${editingTld.tld}` : "Create TLD"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody withoutFooter className="space-y-4">
            <DialogDescription>
              {editingTld
                ? "Update the TLD catalog entry and retail pricing"
                : "Add a new sellable top-level domain to the catalog"}
            </DialogDescription>
            <TldCatalogForm
              tld={editingTld ?? undefined}
              onSubmit={handleSubmit}
              onCancel={() => {
                setDialogOpen(false);
                setEditingTld(null);
              }}
              isSubmitting={isSubmitting}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}
